/**
 * Карточка дела: боковая навигация по блокам + область содержимого (ТЗ FR-02).
 *
 * Пошаговый мастер не применяется сознательно: пользователь-профессионал
 * должен иметь произвольный доступ к любому блоку. Дело редко заполняется
 * сверху вниз — сведения приходят по мере получения ответов от органов.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { BLOCK_TITLES, CASE_PRESETS, isPresetApplicable, type BlockId } from '@bd/domain';
import type { CaseContext } from '../../bx24.js';
import { currentUser } from '../../storage/cases.js';
import { useProfile } from './useProfile.js';
import { useAutosave } from './useAutosave.js';
import { useLock } from './useLock.js';
import { ReadinessPanel } from '../readiness/ReadinessPanel.js';
import { GeneratePanel } from '../generation/GeneratePanel.js';
import { PreviewPane } from '../generation/PreviewPane.js';
import { HistoryPanel } from './HistoryPanel.js';
import { CrmImportBanner } from './CrmImportBanner.js';
import { DebtorBlock } from './blocks/DebtorBlock.js';
import { CourtBlock } from './blocks/CourtBlock.js';
import { RepresentativeBlock } from './blocks/RepresentativeBlock.js';
import { CreditorsBlock } from './blocks/CreditorsBlock.js';
import { PropertyBlock } from './blocks/PropertyBlock.js';
import { DealsBlock } from './blocks/DealsBlock.js';
import { FamilyBlock } from './blocks/FamilyBlock.js';
import { IncomeBlock } from './blocks/IncomeBlock.js';
import { LegalBlock } from './blocks/LegalBlock.js';
import { api } from '../../api.js';

type Tab = BlockId | 'generate' | 'preview' | 'history';

export function CasePage({ context }: { context: CaseContext }) {
  const { profile, loading, error, dirty, folderId, item, prefilled, update, save, replace, readiness } =
    useProfile(context);
  const [user, setUser] = useState<{ id: string; name: string } | null>(null);
  const lock = useLock(context.caseId, user);
  const [tab, setTab] = useState<Tab>('debtor');

  // Кто работает с делом: нужно блокировке (FR-05) и журналу (FR-48)
  useEffect(() => {
    void currentUser()
      .then(setUser)
      .catch(() => setUser({ id: '', name: '' }));
  }, []);

  const readOnly = lock.readOnly;
  const autosave = useAutosave({
    dirty,
    profile,
    save: () => save(user ?? undefined),
    readOnly,
  });

  /** Сообщение об ошибке по пути поля — общее для всех блоков. */
  const errorFor = useCallback(
    (path: string): string | null => readiness?.issues.find((issue) => issue.path === path)?.message ?? null,
    [readiness],
  );

  /** Предзаполнение суда по субъекту РФ адреса регистрации (правило R-01). */
  const onRegionDetected = useCallback(
    async (regionCode: string) => {
      if (profile?.court.id) return; // выбранный вручную суд не перебиваем
      try {
        const { court } = await api.courtByRegion(regionCode);
        if (!court) return;
        update((p) => ({
          ...p,
          court: { id: String(court.id ?? ''), name: court.name, address: court.address ?? '', regionCode },
        }));
      } catch {
        // Справочник недоступен — суд выбирается вручную
      }
    },
    [profile?.court.id, update],
  );

  const blockProps = useMemo(
    () => (profile ? { profile, update, readOnly, errorFor } : null),
    [profile, update, readOnly, errorFor],
  );

  if (loading) return <div className="loading">Загрузка дела…</div>;
  if (!profile || !readiness || !blockProps) return <div className="error-box">Не удалось открыть дело: {error}</div>;

  return (
    <div className="case-page">
      <header className="case-header">
        <div>
          <h1>{[profile.debtor.last, profile.debtor.first, profile.debtor.middle].filter(Boolean).join(' ') || 'Новое дело'}</h1>
          <span className="hint">Дело № {context.caseId} · редакция профиля {profile.revision}</span>
        </div>

        <div className="case-status">
          {/* Индикатор состояния сохранения (ТЗ FR-03) */}
          {autosave.status === 'saving' && <span className="saving">Сохранение…</span>}
          {autosave.status === 'saved' && <span className="saved">Сохранено {autosave.savedAt?.toLocaleTimeString('ru-RU')}</span>}
          {autosave.status === 'error' && <span className="error-text">Не сохранено: {autosave.error}</span>}
          {dirty && autosave.status !== 'saving' && <span className="hint">есть несохранённые изменения</span>}
          <button type="button" className="link-button" disabled={readOnly || !dirty} onClick={() => void autosave.flush()}>
            Сохранить сейчас
          </button>
        </div>
      </header>

      {/* Чек-лист сделки уже заполнен менеджером — повторный ввод не нужен (FR-06) */}
      <CrmImportBanner
        entityTypeId={context.entityTypeId}
        caseId={context.caseId}
        item={item ?? null}
        profile={profile}
        readOnly={readOnly}
        prefilled={prefilled ?? []}
        onApply={(next) => update(() => next)}
      />

      {/* Заготовка предлагается только пустому делу: она ничего не затирает (FR-45) */}
      {!readOnly && isPresetApplicable(profile) && (
        <div className="banner banner-hint">
          Дело пустое. Можно сразу выставить признаки самого частого сочетания:
          {CASE_PRESETS.map((preset) => (
            <button
              key={preset.id}
              type="button"
              className="link-button"
              title={preset.description}
              onClick={() => update((p) => preset.apply(p))}
            >
              {preset.title} — {preset.description}
            </button>
          ))}
        </div>
      )}

      {readOnly && (
        <div className="banner">
          Дело открыто пользователем {lock.heldBy ?? 'другим пользователем'} — режим только для чтения.
          Изменения сохранить не получится.
        </div>
      )}

      <div className="case-layout">
        <nav className="case-nav">
          {(Object.keys(BLOCK_TITLES) as BlockId[]).map((block) => (
            <button
              key={block}
              type="button"
              className={tab === block ? 'nav-item active' : 'nav-item'}
              onClick={() => setTab(block)}
            >
              {BLOCK_TITLES[block]}
              {readiness.blocks[block] === 'errors' && <span className="dot error" title="Есть ошибки" />}
              {readiness.blocks[block] === 'warnings' && <span className="dot warning" title="Есть предупреждения" />}
            </button>
          ))}

          <hr />

          <button type="button" className={tab === 'preview' ? 'nav-item active' : 'nav-item'} onClick={() => setTab('preview')}>
            Предпросмотр
          </button>
          <button type="button" className={tab === 'generate' ? 'nav-item active' : 'nav-item'} onClick={() => setTab('generate')}>
            Формирование пакета
          </button>
          <button type="button" className={tab === 'history' ? 'nav-item active' : 'nav-item'} onClick={() => setTab('history')}>
            История дела
          </button>
        </nav>

        <main className="case-content">
          {tab === 'debtor' && <DebtorBlock {...blockProps} onRegionDetected={onRegionDetected} />}
          {tab === 'court' && <CourtBlock {...blockProps} />}
          {tab === 'representative' && <RepresentativeBlock {...blockProps} />}
          {tab === 'creditors' && <CreditorsBlock {...blockProps} />}
          {tab === 'property' && <PropertyBlock {...blockProps} />}
          {tab === 'deals' && <DealsBlock {...blockProps} />}
          {tab === 'family' && <FamilyBlock {...blockProps} />}
          {tab === 'income' && <IncomeBlock {...blockProps} />}
          {tab === 'legal' && <LegalBlock {...blockProps} />}
          {tab === 'preview' && <PreviewPane profile={profile} />}
          {tab === 'history' && (
            <HistoryPanel
              caseId={context.caseId}
              readOnly={readOnly}
              onRestored={async (restored) => {
                await replace(restored, user ?? undefined);
              }}
            />
          )}
          {tab === 'generate' && (
            <GeneratePanel
              caseId={context.caseId}
              profile={profile}
              readiness={readiness}
              readOnly={readOnly}
              folderId={folderId}
              beforeGenerate={async () => {
                await autosave.flush();
                return save(user ?? undefined);
              }}
            />
          )}
        </main>

        <ReadinessPanel readiness={readiness} activeBlock={tab as BlockId} onSelectBlock={(block) => setTab(block)} />
      </div>
    </div>
  );
}
